import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-10">
      <section className="bg-dark-green py-20 text-white text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('contact.header.title')}</h1>
          <p className="text-xl text-gold-light max-w-2xl mx-auto">
            {t('contact.header.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-dark-green mb-8">{t('contact.getInTouch')}</h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="bg-cream-off p-4 rounded-xl text-gold-primary">
                    <MapPin size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark-green mb-2">{t('contact.info.address.title')}</h4>
                    <p className="text-gray-600 leading-relaxed">
                      {t('contact.info.address.value')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-cream-off p-4 rounded-xl text-gold-primary">
                    <Phone size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark-green mb-2">{t('contact.info.phone.title')}</h4>
                    <p className="text-gray-600">+91 9136556703</p>
                    <p className="text-gray-600">+91 8936835037</p>
                    <p className="text-gray-600 text-sm italic mt-1">{t('contact.info.phone.note')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-cream-off p-4 rounded-xl text-gold-primary">
                    <Mail size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark-green mb-2">{t('contact.info.email.title')}</h4>
                    <p className="text-gray-600">vskassociates91665@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-cream-off p-4 rounded-xl text-gold-primary">
                    <Clock size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-dark-green mb-2">{t('contact.info.hours.title')}</h4>
                    <p className="text-gray-600">{t('contact.info.hours.weekdays')}</p>
                    <p className="text-gray-600">{t('contact.info.hours.sunday')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 flex flex-wrap gap-4">
                <a href="https://wa.me/919136556703" className="flex items-center space-x-3 bg-green-whatsapp text-white px-8 py-4 rounded-xl font-bold hover:opacity-90 transition-opacity">
                  <MessageCircle size={24} />
                  <span>{t('contact.whatsapp')}</span>
                </a>
                <a href="tel:9136556703" className="flex items-center space-x-3 bg-dark-green text-white px-8 py-4 rounded-xl font-bold hover:bg-dark-green-deep transition-colors">
                  <Phone size={24} />
                  <span>{t('contact.callNow')}</span>
                </a>
              </div>
            </div>

            {/* Map Integration */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white h-[500px]">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d115132.86107244517!2d86.511394553228!3d25.882415174068367!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ee43f218b0676d%3A0xc6657c9a6a84d26b!2sSaharsa%2C%20Bihar!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Form Banner */}
      <section className="py-20 bg-cream-off">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-dark-green">{t('contact.form.title')}</h2>
            <p className="text-gray-600 mt-4">{t('contact.form.subtitle')}</p>
          </div>
          <div className="bg-white p-10 rounded-2xl shadow-xl">
             <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-dark-green mb-2">{t('contact.form.name')}</label>
                    <input type="text" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-green mb-2">{t('contact.form.email')}</label>
                    <input type="email" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-dark-green mb-2">{t('contact.form.subject')}</label>
                  <input type="text" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-dark-green mb-2">{t('contact.form.message')}</label>
                  <textarea rows="5" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary outline-none resize-none"></textarea>
                </div>
                <button type="submit" className="w-full bg-dark-green hover:bg-dark-green-deep text-white font-bold py-4 rounded-lg shadow-lg transition-all">
                  {t('contact.form.send')}
                </button>
             </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
